import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { requireLogin } from "../utils/authRedirect";
import { flyToCart } from "../utils/flyToCart";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs, Zoom, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import "swiper/css/zoom";

// Import Fancybox
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import { toast } from "react-toastify";
import { Shrink } from "lucide-react";
import "../styles/global.css";
import { getProductReviewsWithSamples, getProductRatingSummary, getReviewerAvatarUrl, DEFAULT_REVIEWER_AVATAR } from "../utils/productReviews";

const ProductDetail = () => {

  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [imagesSwip, setImages] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [qty, setQty] = useState(1);
  const [userId, setUserId] = useState(localStorage.getItem("user_id"));
  // IDs of every product in the logged-in user's wishlist
  const [wishlistIds, setWishlistIds] = useState([]);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const [url, setUrl] = useState("");

  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });

  // Real (admin-approved) reviews, newest first, topped up with 3-4 sample
  // reviews per product so every product page always shows reviews. Real
  // reviews are never hidden or reordered relative to each other.
  const displayReviews = getProductReviewsWithSamples(product, reviews);
  const displayReviewStats = getProductRatingSummary(product, reviewStats.average_rating, reviewStats.total_reviews);

  const fallbackImage = `${API_BASE_URL}public/noimage.jpg`;
  // Products sharing the same Common Name (generic_name), returned by the API
  const similarProducts = product?.productGenericData || [];
  const images = imagesSwip?.length ? imagesSwip : [fallbackImage];

  // Initialize Fancybox
  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]', {
      Thumbs: {
        autoStart: true,
      },
      Toolbar: {
        display: {
          left: ["infobar"],
          middle: [
            "zoomIn",
            "zoomOut",
            "toggle1to1",
            "rotateCCW",
            "rotateCW",
            "flipX",
            "flipY",
          ],
          right: ["slideshow", "thumbs", "close"],
        },
      },
    });

    return () => {
      Fancybox.destroy();
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setUrl(encodeURIComponent(window.location.href));
    }
  }, []);

  const handleMinus = () => {
    setQty(prev => (prev > 1 ? prev - 1 : 1));
  };

  const handlePlus = () => {
    setQty(prev => prev + 1);
  };

  const fetchProduct = async (slug) => {
    const response = await fetch(`${API_BASE_URL}api/product/${slug}`);
    const json = await response.json();
    if (json.status) {
      setProduct(json.data);
      const images = json.data.images ? json.data.images.split(",") : [];
      setImages(images);
    }
  };

  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (slug) {
      fetchProduct(slug);
    }
  }, [slug]);

  // Only reviews an admin has approved (status = Active) come back from this endpoint.
  useEffect(() => {
    if (!product?.id) { setReviews([]); setReviewStats({ average_rating: 0, total_reviews: 0 }); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/product-reviews/${product.id}`);
        const data = await res.json();
        if (data?.status) {
          setReviews(data.data || []);
          setReviewStats({ average_rating: data.average_rating || 0, total_reviews: data.total_reviews || 0 });
        }
      } catch (err) {
        console.log("product reviews fetch error:", err);
      }
    })();
  }, [product?.id]);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("device_id", deviceId);
  }

  const addToCart = (id, qty) => {
    const payload = {
      device_id: deviceId,
      id: id,
      qty: qty,
    };

    console.log(payload);

    fetch(`${API_BASE_URL}api/addToCart`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((json) => {
        const count = Object.keys(json.cart).length;
        setCartCount(count);
        const el = document.querySelector(".cartcount");
        if (el) el.innerText = count;

        console.log("Cart Updated:", json.cart, count);
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count } }));
      })
      .catch((err) => console.error("Add to cart error:", err));
  };

  // Load the user's wishlist once so hearts show the correct state
  useEffect(() => {
    const uid = localStorage.getItem("user_id");
    if (!uid) return;
    fetch(`${API_BASE_URL}api/wishlist?userid=${uid}`)
      .then((r) => r.json())
      .then((j) => {
        if (j?.status && Array.isArray(j.data)) setWishlistIds(j.data.map(Number));
      })
      .catch((err) => console.error("Wishlist load error:", err));
  }, [userId]);

  const isWishlisted = (id) => wishlistIds.includes(Number(id));
  const wishlisted = product ? isWishlisted(product.id) : false;

  // Add or remove a product (the API toggles: 201 = added, 409 = removed)
  const toggleWishlist = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireLogin(navigate, location, { message: "Please login to use your wishlist" })) return;
    if (!productId || wishlistBusy) return;

    const userid = localStorage.getItem("user_id");
    setWishlistBusy(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/wishlist/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ product_id: productId, userid }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 201) {
        setWishlistIds((prev) => [...new Set([...prev, Number(productId)])]);
        toast.success("Added to wishlist");
      } else if (response.status === 409) {
        setWishlistIds((prev) => prev.filter((id) => id !== Number(productId)));
        toast.info("Removed from wishlist");
      } else {
        toast.error(data?.message || "Could not update wishlist. Please try again.");
      }
    } catch (error) {
      console.error("Wishlist error:", error);
      toast.error("Could not update wishlist. Please try again.");
    } finally {
      setWishlistBusy(false);
    }
  };

  const handleWishlist = (e) => toggleWishlist(e, product?.id);

  // ── Inline styles using your CSS variables ──────────────────────────────

  const styles = {
    // Wishlist / Share circular icon button
    whisList: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      borderRadius: "50%",
      border: "1.5px solid var(--grad-main)",
      background: "var(--light)",
      color: "var(--grad-main)",
      fontSize: "15px",
      cursor: "pointer",
      transition: "all 0.22s ease",
      textDecoration: "none",
      flexShrink: "0",
    },
    whisListActive: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      borderRadius: "50%",
      border: "1.5px solid #58A186",
      background: "var(--grad-main)",
      color: "#ffffff",
      fontSize: "15px",
      cursor: "pointer",
      transition: "all 0.22s ease",
      textDecoration: "none",
    },
    // Size button – default (unselected)
    sizeBtn: {
      background: "var(--light)",
      color: "var(--grad-main)",
      border: "1.5px solid var(--grad-main)",
      borderRadius: "50px",
      fontSize: "13px",
      padding: "6px 16px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.22s ease",
    },
    // Size button – active / selected
    sizeBtnSelected: {
      background: "var(--grad-main)",
      color: "var(--light)",
      border: "1.5px solid var(--grad-main)",
      borderRadius: "50px",
      fontSize: "13px",
      padding: "6px 16px",
      fontWeight: "700",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(88,161,134,0.30)",
      transition: "all 0.22s ease",
    },
    // In Stock badge
    inStock: {
      display: "inline-block",
      padding: "6px 22px",
      borderRadius: "50px",
      background: "#d1fae5",
      color: "var(--grad-main)",
      border: "1.5px solid #6ee7b7",
      fontSize: "13px",
      fontWeight: "600",
      pointerEvents: "none",
    },
    // Out of Stock badge
    outOfStock: {
      display: "inline-block",
      padding: "6px 22px",
      borderRadius: "50px",
      background: "#fee2e2",
      color: "#991b1b",
      border: "1.5px solid #fca5a5",
      fontSize: "13px",
      fontWeight: "600",
      opacity: "1",
      cursor: "not-allowed",
      marginBottom: "10px"
    },
    // Add to Cart primary button
    btnCart: {
      // background: "linear-gradient(135deg, #1E7A8C 0%, #238495 100%)",
      color: "var(--light)",
      border: "1.5px solid var(--grad-main)",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: "500",
      padding: "10px 22px",
      cursor: "pointer",
      transition: "background 0.22s ease, transform 0.15s ease",
    },
    // Wishlist outline button (mobile)
    btnWishlistOutline: {
      background: "transparent",
      color: "var(--grad-main)",
      border: "1.5px solid var(--grad-main)",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: "500",
      padding: "10px 22px",
      cursor: "pointer",
      transition: "all 0.22s ease",
    },
    // View Cart button
    btnViewCart: {
      // background: "linear-gradient(135deg, #1E7A8C 0%, #238495 100%)",
      color: "var(--light)",
      border: "1.5px solid #58A186",
    },
  };

  return (
    <>
      <Header />
      <section className="cl-breadcrumb-bar py-xl-3 py-3">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <nav aria-label="breadcrumb">
               <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>

                  <li className="breadcrumb-item">
                    <Link to={`/category/${product?.category_title?.slug || ""}`}>
                      {product?.category_title?.name || "Category"}
                    </Link>
                  </li>

                  <li className="breadcrumb-item active" aria-current="page">
                    {product?.name || "Product Detail"}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <section className="product-detail pb-5 pt-0 mt-3">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 col-xl-4 my-thumb-1">
              {/* Main Slider */}
              <Swiper
                modules={[Navigation, Pagination, Thumbs, Zoom]}
                navigation
                zoom={{ maxRatio: 3 }}
                pagination={{ clickable: true }}
                thumbs={{ swiper: thumbsSwiper }}
                className="mySwiper2"
              >
                {images.map((img, index) => (
                  <SwiperSlide key={index}>
                    <div className="swiper-zoom-container">
                      <a
                        href={`${API_BASE_URL}public/productimages/${img}`}
                        data-fancybox="gallery"
                        data-caption={`${product?.name} - Image ${index + 1}`}
                      >
                        <img
                          src={
                            imagesSwip?.length
                              ? `${API_BASE_URL}public/productimages/${img}`
                              : fallbackImage
                          }
                          alt={`image-${index}`}
                        />
                      </a>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Thumbnail Slider with Navigation Arrows */}
              <Swiper
                modules={[Navigation, Thumbs]}
                onSwiper={setThumbsSwiper}
                slidesPerView={4}
                spaceBetween={10}
                watchSlidesProgress
                navigation={{
                  nextEl: '.swiper-button-next-thumb',
                  prevEl: '.swiper-button-prev-thumb',
                }}
                className="mySwiper thumb-swiper d-xl-block d-none"
              >
                {imagesSwip?.map((img, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={
                        imagesSwip?.length
                          ? `${API_BASE_URL}public/productimages/${img}`
                          : fallbackImage
                      }
                      alt={`thumb-${index}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/no-image.png";
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  </SwiperSlide>
                ))}
                <div className="swiper-button-prev-thumb swiper-button-thumb">
                  <i className="fa-solid fa-chevron-left"></i>
                </div>
                <div className="swiper-button-next-thumb swiper-button-thumb">
                  <i className="fa-solid fa-chevron-right"></i>
                </div>
              </Swiper>
            </div>

            <div className="col-lg-6 col-xl-5">
              <h6 className="thin-heading">{product?.category_title?.name} ( {product?.weight} {product?.medicine_title?.name})</h6>
              <h4>{product?.name}</h4>
              <p>{product?.short_desc}</p>
              <div className="d-flex align-items-center gap-3 mb-2">
                <div className="rating mb-0">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <i key={n} className={n <= Math.round(displayReviewStats.average || 0) ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                  ))}
                </div>
                <span className="rating-text mb-0">
                  {displayReviewStats.count > 0 ? `(${displayReviewStats.count} rating${displayReviewStats.count !== 1 ? "s" : ""})` : "(No ratings yet)"}
                </span>
              </div>

              <div className="feature_like_share">
                {/* ── Wishlist button (desktop) ── */}
                <button
                  type="button"
                  className="d-md-flex d-none"
                  style={{ ...(wishlisted ? styles.whisListActive : styles.whisList), cursor: "pointer" }}
                  aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                  title={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                  disabled={wishlistBusy}
                  onClick={handleWishlist}
                >
                  <i className={wishlisted ? "fa-solid fa-heart" : "fa-regular fa-heart"}></i>
                </button>

                {/* ── Share button (desktop) ── */}
                <div ref={ref} className="position-relative">
                  <a
                    href="#"
                    className="d-md-flex d-none"
                    style={styles.whisList}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpen(!open);
                    }}
                  >
                    <i className="fa-solid fa-share-nodes"></i>
                  </a>

                  {open && (
                    <ul className="dropdown-menu Social-media-share show">
                      <li>
                        <a
                          className="rounded-pill facebook-bg h1 w-100"
                          href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                          target="_blank"
                        >
                          <span className="product-social-icons">
                            <i className="fa-brands fa-facebook-f"></i>
                          </span>
                          Facebook
                        </a>
                      </li>
                      <li>
                        <a
                          className="rounded-pill whatsapp-bg h1 w-100"
                          href={`https://wa.me/?text=${url}`}
                          target="_blank"
                        >
                          <span className="product-social-icons">
                            <i className="fa-brands fa-whatsapp"></i>
                          </span>
                          Whatsapp
                        </a>
                      </li>
                      <li>
                        <a
                          className="rounded-pill twitter-bg h1 w-100"
                          href={`https://twitter.com/intent/tweet?url=${url}`}
                          target="_blank"
                        >
                          <span className="product-social-icons">
                            <i className="fa-brands fa-x-twitter"></i>
                          </span>
                          Twitter
                        </a>
                      </li>
                    </ul>
                  )}
                </div>
              </div>

              <div className="price-tag">
                {product?.dprice && <del>{product?.price}</del>}
                {product?.dprice && <span>₹ {product?.price - (product?.price * product?.dprice / 100)}</span>}
                {!product?.dprice && <span>₹ {product?.price}</span>}
              </div>
              <div className="tax-text">Inclusive of all taxes</div>
              <hr />

              {/* ── Size / Volume selector ── */}
              <div className="product-size">
                <p className="">Size/Volume</p>
                <ul className="product-size-values">
                  {product?.productGenericData?.length > 0 ? (
                    product?.productGenericData?.map((productSingle) => (
                      <li className="button-selectable" key={productSingle.slug}>
                        <Link to={`/product/${productSingle.slug}`} fdprocessedid="2hn44">
                          <button
                            style={
                              productSingle.slug === slug
                                ? styles.sizeBtnSelected
                                : styles.sizeBtn
                            }
                            className="text-capitalize"
                          >
                            {productSingle?.weight} {productSingle?.medicine_title?.name}
                          </button>
                        </Link>
                      </li>
                    ))
                  ) : (
                    <p> </p>
                  )}
                </ul>
              </div>

              <form className="d-flex justify-content-start flex-column mb-xl-3 mb-3">
                {/* ── In Stock / Out of Stock badge ── */}
                <div className="add_cart_block d-none d-md-block">
                  {(product?.stock_available === 'In Stock' && product?.sku !== null && product?.sku !== undefined && Number(product?.sku) > 0) ? (
                    <button style={styles.inStock} type="button">
                      In Stock
                    </button>
                  ) : (
                    <button style={styles.outOfStock} type="button" disabled>
                      Out of Stock
                    </button>
                  )}
                </div>
                {product?.stock_available !== 'In Stock' && (
                  <div className="d-inline-flex align-items-center gap-3">
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="Enter Your Phone Number"
                    />
                    <button type="submit" className="dc-book-btn py-2">
                      <span>Notify Me</span>
                    </button>
                  </div>
                )}
              </form>

              {/* ── Mobile action bar ── */}
              <div className="add_cart_mobile add_cart_mobile justify-content-between" style={{ zIndex: 999 }}>
                <a
                  href="#"
                  className="d-lg-none d-flex shrink-0"
                  style={styles.whisList}
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasBottom"
                  aria-controls="offcanvasBottom"
                >
                  <i className="fa-solid fa-share-nodes"></i>
                </a>

                {/* Wishlist outline button (mobile) */}
                <button
                  type="button"
                  className="w-50 d-flex align-items-center justify-content-center rounded-pill gap-2"
                  style={{ ...styles.btnWishlistOutline, cursor: "pointer" }}
                  disabled={wishlistBusy}
                  onClick={handleWishlist}
                >
                  <i className={wishlisted ? "fa-solid fa-heart" : "fa-regular fa-heart"}></i>
                  {" "}{wishlisted ? "Wishlisted" : "Wishlist"}
                </button>

                {product?.stock_available === 'In Stock' ? (
                  <button
                    type="button"
                    className="w-50  rounded-pill dc-book-btn d-flexx align-items-center justify-content-center"
                    style={styles.btnCart}
                    onClick={() => {
                      flyToCart(document.querySelector(".mySwiper2 .swiper-slide-active img"));
                      addToCart(product?.id, qty);
                    }}
                  >
                    <i className="fa-solid fa-cart-shopping"></i> <span>Add to Cart</span>
                  </button>
                ) : (
                  <button
                    style={styles.outOfStock}
                    className="w-50 text-uppercase"
                    disabled
                  >
                    Out of Stock
                  </button>
                )}
              </div>

              {/* ── Offcanvas share (mobile) ── */}
              <div className="offcanvas offcanvas-bottom" tabIndex="-1" id="offcanvasBottom" aria-labelledby="offcanvasBottomLabel" style={{ zIndex: 1050 }}>
                <div className="offcanvas-header">
                  <h5 className="offcanvas-title" id="offcanvasBottomLabel">Share</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                </div>
                <div className="offcanvas-body small p-0">
                  <div className="cart_list">
                    <div className="d-flex cart_list_card">
                      <div className="flex-shrink-0 cart_list_img">
                        <img
                          src={
                            product?.images
                              ? `${API_BASE_URL}public/productimages/${product?.images.split(",")[0]}`
                              : `${API_BASE_URL}public/noimage.jpg`
                          }
                          className="product-img"
                          alt={product?.name}
                        />
                      </div>
                      <div className="flex-grow-1 cart_list_info">
                        <div className="cart_list_title"><a href="#" className="h6">{product?.name}</a></div>
                        <div className="cart_list_price">
                          {product?.dprice ? (
                            <>
                              <del>₹ {product?.price}</del>
                              <span>₹ {product?.price - (product?.price * product?.dprice / 100)}</span>
                            </>
                          ) : (
                            <span>₹ {product?.price}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="social-share mb-3">
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" className="btn rounded-pill facebook-bg btn-sm h1"><i className="fa-brands fa-facebook-f"></i></a>
                    <a href={`https://wa.me/?text=${url}`} target="_blank" className="btn rounded-pill twitter-bg btn-sm h1"><i className="fa-brands fa-twitter"></i></a>
                    <a href={`https://twitter.com/intent/tweet?url=${url}`} target="_blank" className="btn rounded-pill whatsapp-bg btn-sm h1"><i className="fa-brands fa-whatsapp"></i></a>
                  </div>
                </div>
              </div>

              {/* ── Qty + Add to Cart (desktop) ── */}
              {product?.stock_available === 'In Stock' && (
                <div className="d-flex align-items-center gap-3 mb-4">
                  <span className="d-md-none d-block fw-bold">Qty:</span>
                  <div className="qty-container">
                    <button
                      className="qty-btn-minus btn-light"
                      type="button"
                      onClick={handleMinus}
                    >
                      <i className="fa fa-minus"></i>
                    </button>
                   <input
                      type="text"
                      name="qty"
                      value={qty}
                      className="input-qty"
                      min={1}
                      readOnly
                    />
                    <button
                      className="qty-btn-plus btn-light"
                      type="button"
                      onClick={handlePlus}
                    >
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  {/* Add to Cart – desktop */}
                  <button
                    type="button"
                    className="dc-book-btn d-md-block d-none rounded-pill"
                    style={styles.btnCart}
                    onClick={() => {
                      flyToCart(document.querySelector(".mySwiper2 .swiper-slide-active img"));
                      addToCart(product?.id, qty);
                    }}
                  >
                    <i className="fa-solid fa-cart-shopping"></i><span>Add To Cart</span>
                  </button>
                </div>
              )}
            </div>

            <div className="col-lg-6 col-xl-3 d-xl-block d-none">
              <div className="add-item">
                <p className="mb-2 fw-bold">Please add item(s) to proceed</p>
                <Link
                  to="/cart"
                  className="dc-book-btn w-100 justify-content-center d-flex align-items-center "
                  style={styles.btnViewCart}
                  fdprocessedid="2hn44"
                >
                  <i className="fa-solid fa-cart-shopping"></i><span>View Cart</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tabs: Description / Product Details / FAQ ── */}
      <section className="tab-section content">
        <ul className="nav nav-tabs d-flex align-items-center justify-content-center" id="myTab" role="tablist">
          <li className="nav-item" role="presentation">
            <button className="nav-link active" id="home-tab" data-bs-toggle="tab" data-bs-target="#home-tab-pane" type="button"
              role="tab" aria-controls="home-tab-pane" aria-selected="true">Description</button>
          </li>
          <li className="nav-item" role="presentation">
            <button className="nav-link" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile-tab-pane" type="button"
              role="tab" aria-controls="profile-tab-pane" aria-selected="false">Product Details</button>
          </li>
          <li className="nav-item">
            <button
              className="nav-link"
              id="ingredients-tab"
              data-bs-toggle="tab"
              data-bs-target="#ingredients-tab-pane"
            >
              Ingredients
            </button>
          </li>

          <li className="nav-item">
            <button
              className="nav-link"
              id="howto-tab"
              data-bs-toggle="tab"
              data-bs-target="#howto-tab-pane"
            >
              How To Use
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className="nav-link" id="contact-tab" data-bs-toggle="tab" data-bs-target="#contact-tab-pane" type="button"
              role="tab" aria-controls="contact-tab-pane" aria-selected="false">FAQ</button>
          </li>
        </ul>
        <div className="tab-content" id="myTabContent">
          <div className="tab-pane fade show active" id="home-tab-pane" role="tabpanel" aria-labelledby="home-tab" tabIndex="0">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="grey-bg">
                    <h4>Description</h4>
                    <div dangerouslySetInnerHTML={{ __html: product?.description }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
              className="tab-pane fade"
              id="ingredients-tab-pane"
              role="tabpanel"
            >
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <div className="grey-bg">
                      <h4>Ingredients</h4>

                      {product?.ingredients ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.ingredients
                          }}
                        />
                      ) : (
                        <p>No ingredients available.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="tab-pane fade"
              id="howto-tab-pane"
              role="tabpanel"
            >
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <div className="grey-bg">
                      <h4>How To Use</h4>

                       {product?.how_to_use &&
                          (() => {
                            try {
                              return JSON.parse(product.how_to_use).map((item, index) => (
                                <div key={index} className="mb-4 pb-3 border-bottom">
                                  <h5><strong>Step {index + 1}:</strong> {item.question}</h5>
                                  <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                                </div>
                              ));
                            } catch {
                              return <div dangerouslySetInnerHTML={{ __html: product.how_to_use }} />;
                            }
                          })()
                        }
                    </div>
                  </div>
                </div>
              </div>
            </div>


          <div className="tab-pane fade" id="profile-tab-pane" role="tabpanel" aria-labelledby="profile-tab" tabIndex="0">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="grey-bg">
                    <ul className="product_spec">
                      <li><span className="spec_property"><b>Brand : </b></span><span className="spec_value"> {product?.brand} </span></li>
                      <li><span className="spec_property"><b>Model : </b></span><span className="spec_value"> {product?.model}</span></li>
                      <li><span className="spec_property"><b>Color : </b></span><span className="spec_value"> {product?.color}</span></li>
                      <li><span className="spec_property"><b>Relase Date : </b></span><span className="spec_value"> {product?.releaseDate}</span></li>
                      <li>
                        <span className="spec_property"><b>Manufacturer Details : </b></span>
                        <span className="spec_value">
                          <div dangerouslySetInnerHTML={{ __html: product?.features }} />
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tab-pane fade" id="contact-tab-pane" role="tabpanel" aria-labelledby="contact-tab" tabIndex="0">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="grey-bg">
                    <h4>FAQ</h4>

                    {product?.faq &&
                        (() => {
                          try {
                            return JSON.parse(product.faq).map((item, index) => (
                              <div key={index} className="mb-4 pb-3 border-bottom">
                                <h5><strong>Q{index + 1}:</strong> {item.question}</h5>
                                <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                              </div>
                            ));
                          } catch {
                            return <div dangerouslySetInnerHTML={{ __html: product.faq }} />;
                          }
                        })()
                      }
 
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Similar Products ── */}
      {similarProducts.length > 0 && (
      <section className="simlar-product section-padding">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2>Similar Products</h2>
              <Swiper
                key={product?.id}
                modules={[Navigation, Autoplay]}
                spaceBetween={10}
                slidesPerView={4}
                navigation
                /* loop only when there are more slides than are visible */
                loop={similarProducts.length > 4}
                autoplay={{ delay: 3000 }}
                breakpoints={{
                  0: { slidesPerView: 1 },
                  600: { slidesPerView: 2 },
                  1000: { slidesPerView: 4 },
                }}
              >
                {similarProducts.map(productSingle => (
                  <SwiperSlide key={productSingle.id}>
                    <div className="pv-prod-card">
                      <span className="pv-prod-tag"><i className="fa-solid fa-circle-check" /> {productSingle.dprice ? `${productSingle.dprice}% Off` : "Hot Sale"}</span>
                      <div className="pv-prod-img">
                        <Link to={`/product/${productSingle.slug}`}>
                          <img
                            src={
                              productSingle.images
                                ? `${API_BASE_URL}public/productimages/${productSingle.images.split(",")[0]}`
                                : `${API_BASE_URL}public/noimage.jpg`
                            }
                            alt={productSingle.name}
                          />
                        </Link>
                        <button
                          type="button"
                          className="pv-prod-wish"
                          aria-label={isWishlisted(productSingle.id) ? "Remove from wishlist" : "Add to wishlist"}
                          onClick={(e) => toggleWishlist(e, productSingle.id)}
                        >
                          <i className={isWishlisted(productSingle.id) ? "fa-solid fa-heart" : "fa-regular fa-heart"} />
                        </button>
                      </div>
                      <div className="pv-prod-body">
                        <p className="pv-prod-cat">{productSingle.category_title?.name}</p>
                        <Link to={`/product/${productSingle.slug}`}>
                          <h4 className="pv-prod-name">
                            {productSingle.name} ({productSingle?.weight} {productSingle?.medicine_title?.name})
                          </h4>
                        </Link>
                        <div className="pv-prod-rating">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <i key={n} className={n <= Math.round(productSingle?.average_rating || 0) ? "fa-solid fa-star" : "fa-regular fa-star"} />
                          ))}
                          <span>{productSingle?.review_count ? `(${productSingle.review_count})` : "(0)"}</span>
                        </div>
                        <div className="pv-prod-price">
                          {productSingle.dprice
                            ? <><b>₹{productSingle.price - (productSingle.price * productSingle.dprice / 100)}</b><del>₹{productSingle.price}</del></>
                            : <b>₹{productSingle.price}</b>}
                        </div>
                        <button
                          type="button"
                          className="pv-prod-cart"
                          onClick={(e) => {
                            flyToCart(e.currentTarget.closest(".pv-prod-card").querySelector(".pv-prod-img"));
                            addToCart(productSingle.id, 1);
                          }}
                        >
                          <i className="fa-solid fa-cart-shopping" /> Add to Cart
                        </button>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Reviews ── */}
      {(displayReviewStats.count > 0 || displayReviews.length > 0) && (
      <section className="content">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 mb-4">
              <div className="grey-bg">
                <div className="row">
                  <div className="col-md-3 col-lg-3 my-auto">
                    <div className="d-flex flex-column align-items-center justify-content-lg-start justify-content-center gap-2 mb-lg-0 mb-3">
                      <div className="d-flex">
                        <div className="review">{displayReviewStats.average || 0}</div>
                        <div className="total-rating">/5</div>
                      </div>
                      <div className="rating mb-0">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <i key={n} className={n <= Math.round(displayReviewStats.average || 0) ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                        ))}
                      </div>
                      <span>
                        {displayReviewStats.count > 0 ? "Verified customer reviews" : "No reviews yet"}
                      </span>
                      <p className="mb-0 text-center">Based on {displayReviewStats.count} review{displayReviewStats.count !== 1 ? "s" : ""}.</p>
                      {displayReviewStats.count > 0 && (
                        <a href="#" className="text-primary fw-bold view-all-review" data-bs-toggle="modal" data-bs-target="#staticBackdrop">View All Reviews</a>
                      )}
                    </div>
                  </div>
                  <div className="col-md-9 col-lg-9"> 

                    {displayReviews.length > 0 ? (
                      <Swiper
                          modules={[Navigation]}
                          navigation
                          spaceBetween={20}
                          slidesPerView={3}
                          breakpoints={{
                            0: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1200: { slidesPerView: 3 }
                          }}
                        >
                          {displayReviews.map((r) => (
                            <SwiperSlide key={r.id}>
                              <div className="review-box bg-white">
                              <div className="user-icon">
                                                      <img
                                                        src={getReviewerAvatarUrl(r, API_BASE_URL)}
                                                        alt=""
                                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_REVIEWER_AVATAR; }}
                                                      />
                                                    </div>
                                                    <div className="rating mb-lg-4 mb-3">
                                                      {[1, 2, 3, 4, 5].map((n) => (
                                                        <i key={n} className={n <= r.rating ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                                                      ))}
                                                    </div>
                                                    <p><strong>{r.reviewer}</strong><br />{r.comment || "No written feedback provided."}</p>
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                    ) : (
                      <p className="text-muted mb-0">Be the first to review this product.</p>
                    )}
                  </div>
                </div>

                {/* Reviews Modal */}
                <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1"
                  aria-labelledby="staticBackdropLabel" aria-hidden="true">
                  <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                      <div className="modal-header bg-main-primary">
                        <h2 className="modal-title fs-5 text-white fw-medium" id="staticBackdropLabel">Reviews</h2>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                      </div>
                      <div className="modal-body">
                        {displayReviews.length > 0 ? displayReviews.map((r) => (
                          <div className="review-box bg-white mb-3" key={r.id}>
                            <div className="user-icon">
                              <img
                                src={getReviewerAvatarUrl(r, API_BASE_URL)}
                                alt=""
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_REVIEWER_AVATAR; }}
                              />
                            </div>
                            <div className="rating mb-lg-4 mb-3">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <i key={n} className={n <= r.rating ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                              ))}
                            </div>
                            <p><strong>{r.reviewer}</strong><br />{r.comment || "No written feedback provided."}</p>
                          </div>
                        )) : (
                          <p className="text-muted mb-0">No reviews yet.</p>
                        )}
                      </div>
                      <div className="modal-footer justify-content-center">
                        <nav aria-label="Page navigation example">
                          <ul className="pagination mb-0">
                            <li className="page-item pb-0">
                              <a className="page-link" href="#" aria-label="Previous">
                                <span aria-hidden="true">&laquo;</span>
                              </a>
                            </li>
                            <li className="page-item pb-0"><a className="page-link active" href="#">1</a></li>
                            <li className="page-item pb-0"><a className="page-link" href="#">2</a></li>
                            <li className="page-item pb-0"><a className="page-link" href="#">3</a></li>
                            <li className="page-item pb-0">
                              <a className="page-link" href="#" aria-label="Next">
                                <span aria-hidden="true">&raquo;</span>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      <Footer />
    </>
  );
};

export default ProductDetail;